import { type ChangeEvent, useEffect, useMemo, useState } from "react";
import { QuestionMarkCircleIcon, RichText, TextInput, ToggleInput } from "@datagame/ui";
import { Trans, useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";

import { AttributesSection } from "../AttributesSection";
import {
  type Game,
  type LeaderboardConfigurationUpdateInput,
  type QuickRankConfigurationUpdateInput,
  QuickRankMode,
} from "../../../../../../../gql/graphql";
import { isQuickPickGame } from "../../../../../../../graphql/helpers/isQuickPickGame";
import {
  LEADERBOARD_CONFIGURATION_UPDATE,
  QUICKRANK_CONFIGURATION_UPDATE,
} from "../../../../../../../graphql/mutations/GameMutation";
import { Section } from "../../Common";
import { SelectInput } from "../../../../../../shared/ui/SelectInput";
import type { SelectOption } from "../../../../../../shared/ui/SelectInput/SelectInput";
import { TooltipWithIcon } from "../../../../../../shared/ui/TooltipWithIcon";

import styles from "./QuickRankSettingsForm.module.scss";
import { isLeaderboardGame } from "../../../../../../../graphql/helpers/LeaderboardGame";
import { Accordion } from "../../../../../../shared/ui/Accordion";
import { LeaderboardConfigSection } from "../LeaderboardConfigSection";
import { useProject } from "../../../../../../../contexts/project/ProjectContext";
import { GAME_ALL } from "../../../../../../../graphql/queries/GameQueries";

export interface QuickRankSettingsFormProps {
  game: Game;
}

export const QuickRankSettingsForm = ({ game }: QuickRankSettingsFormProps) => {
  const { t } = useTranslation();

  const { project } = useProject();
  const modeOptions = useMemo(() => {
    return [
      {
        value: QuickRankMode.TwoPickN,
        label: "2 Pick N",
      },
      {
        value: QuickRankMode.Optimized,
        label: "Optimized",
      },
    ] as SelectOption[];
  }, []);

  const scaleSizeOptions = useMemo(() => {
    return Array.from(Array(10), (_, index) => {
      return { value: (index + 1).toString(), label: (index + 1).toString() };
    });
  }, []);

  const [question, setQuestion] = useState<string | undefined>(undefined);
  const [ratingQuestion, setRatingQuestion] = useState<string>(
    "ratingQuestion" in game.configuration ? game.configuration.ratingQuestion : "",
  );
  const [ratingRightLabel, setRatingRightLabel] = useState<string>(
    "ratingRightLabel" in game.configuration ? game.configuration.ratingRightLabel : "",
  );
  const [ratingLeftLabel, setRatingLeftLabel] = useState<string>(
    "ratingLeftLabel" in game.configuration ? game.configuration.ratingLeftLabel : "",
  );
  const [showRatingScale, setShowRatingScale] = useState(
    "showRatingScale" in game.configuration ? game.configuration.showRatingScale : false,
  );

  const [shuffleCards, setShuffleCards] = useState(
    "shuffleCards" in game.configuration ? game.configuration.shuffleCards : false,
  );

  const [ratingScale, setRatingScale] = useState(
    scaleSizeOptions.find((item) => {
      if ("ratingScaleSize" in game.configuration) {
        return game.configuration.ratingScaleSize === parseInt(item.value);
      }
    }),
  );

  const [mode, setMode] = useState(
    modeOptions.find((item) => {
      if ("mode" in game.configuration) {
        return game.configuration.mode === item.value;
      }
    }),
  );

  const [showLeaderboard, setShowLeaderboard] = useState(
    isLeaderboardGame(game) ? game.configuration.showLeaderboard : false,
  );

  const [updateConfigMutation] = useMutation(QUICKRANK_CONFIGURATION_UPDATE);
  const [updateLeaderboardConfig] = useMutation(LEADERBOARD_CONFIGURATION_UPDATE);

  useEffect(() => {
    setQuestion("question" in game.configuration ? game.configuration.question : "");
  }, []);

  const handleShowLeaderboardChange = (event: ChangeEvent<HTMLInputElement>) => {
    setShowLeaderboard(event.target.checked);

    updateConfigMutation({
      variables: {
        quickRankConfigurationId: game?.configuration.id,
        data: {
          showLeaderboard: event.target.checked,
        },
      },
    });
  };

  const handleLeaderboardConfigurationChange = (data: LeaderboardConfigurationUpdateInput) => {
    if (isQuickPickGame(game) && project) {
      updateLeaderboardConfig({
        variables: {
          leaderboardConfigurationId: game?.configuration.leaderboardConfiguration?.id,
          data,
        },
        refetchQueries: [{ query: GAME_ALL, variables: { projectId: project.id } }],
      });
    }
  };

  const handleQuestionChange = (value: string) => {
    setQuestion(value);
  };

  const updateConfig = (data: QuickRankConfigurationUpdateInput) => {
    updateConfigMutation({
      variables: {
        quickRankConfigurationId: game.configuration.id,
        data,
      },
    });
  };

  const handleModeChange = (item: SelectOption) => {
    const newValue = modeOptions.find((option) => {
      return option.value === item.value;
    });
    setMode(newValue);

    newValue?.value && updateConfig({ mode: newValue.value as QuickRankMode });
  };

  const handleRatingScaleChange = (item: SelectOption) => {
    const newValue = scaleSizeOptions.find((option) => {
      return option.value === item.value;
    });

    setRatingScale(newValue);

    newValue?.value && updateConfig({ ratingScaleSize: parseInt(newValue.value) });
  };

  const handleChangeRatingScale = (event: ChangeEvent<HTMLInputElement>) => {
    setShowRatingScale(event.target.checked);

    updateConfig({ showRatingScale: event.target.checked });
  };

  const handleChangeShuffleCards = (event: ChangeEvent<HTMLInputElement>) => {
    const isShuffled = event.target.checked;

    setShuffleCards(isShuffled);

    updateConfig({ shuffleCards: isShuffled });
  };

  const handleSave = () => {
    updateConfig({ question, ratingQuestion, ratingRightLabel, ratingLeftLabel });
  };

  const renderModeTip = () => {
    if (mode?.value === QuickRankMode.AllUp) {
      return <Trans i18nKey={"quickrank:allUpMode"} />;
    }

    if (mode?.value === QuickRankMode.TwoPickN) {
      return <Trans i18nKey={"quickrank:twoPickNMode"} />;
    }

    if (mode?.value === QuickRankMode.Optimized) {
      return <Trans i18nKey={"quickrank:optimizedMode"} />;
    }
  };

  return (
    <>
      <Section title={t("quickrank:question")} tooltip={<TooltipWithIcon content={t("quickrank:questionTooltip")} />}>
        <RichText
          icon={<QuestionMarkCircleIcon />}
          placeholder={t("quickrank:questionPlaceholder") ?? ""}
          value={question}
          onChange={handleQuestionChange}
          onBlur={handleSave}
          size="small"
          className={styles.input}
        />
      </Section>

      <AttributesSection game={game} />

      <Section
        title={t("quickrank:gameplayRules")}
        headerClassName={styles.gameplaySectionHeader}
        className={styles.gampeplaySection}
      >
        {!isQuickPickGame(game) && (
          <Section
            title={t("quickrank:algorithm")}
            tip={renderModeTip()}
            tooltip={<TooltipWithIcon content={t("quickrank:algorithmTooltip")} />}
          >
            <SelectInput
              options={modeOptions}
              selected={mode}
              onChange={handleModeChange}
              buttonClassName={styles.input}
            />
          </Section>
        )}

        {isQuickPickGame(game) && (
          <Section title="">
            <ToggleInput
              value={shuffleCards}
              name="shuffle-cards"
              onChange={handleChangeShuffleCards}
              label={t("quickrank:shuffle") ?? ""}
              tooltip={<TooltipWithIcon content={t("quickrank:shuffleTooltip")} />}
            />
          </Section>
        )}

        <Section title="">
          <ToggleInput
            value={showRatingScale}
            name="show-rating-scale"
            onChange={handleChangeRatingScale}
            label={t("quickrank:useRatingScale") ?? ""}
            tooltip={<TooltipWithIcon content={t("quickrank:useRatingScaleTooltip")} />}
          />
        </Section>

        {showRatingScale && (
          <>
            <Section
              title={t("quickrank:ratingQuestion")}
              tooltip={<TooltipWithIcon content={t("quickrank:ratingQuestionTooltip")} />}
            >
              <TextInput
                icon={<QuestionMarkCircleIcon />}
                placeholder={t("quickrank:ratingQuestionPlaceholder") ?? ""}
                value={ratingQuestion || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setRatingQuestion(e.target.value)}
                onBlur={handleSave}
                size="small"
                className={styles.input}
              />
            </Section>

            <Section
              title={t("quickrank:ratingScaleSize")}
              tooltip={<TooltipWithIcon content={t("quickrank:ratingScaleSizeTooltip")} />}
            >
              <SelectInput
                options={scaleSizeOptions}
                selected={ratingScale}
                onChange={handleRatingScaleChange}
                buttonClassName={styles.input}
              />
            </Section>

            <Section
              title={t("quickrank:ratingLeftLabel")}
              tooltip={<TooltipWithIcon content={t("quickrank:ratingLeftLabelTooltip")} />}
            >
              <TextInput
                icon={<QuestionMarkCircleIcon />}
                placeholder={t("quickrank:ratingLeftLabelPlaceholder") ?? ""}
                value={ratingLeftLabel || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setRatingLeftLabel(e.target.value)}
                onBlur={handleSave}
                size="small"
                className={styles.input}
              />
            </Section>

            <Section
              title={t("quickrank:ratingRightLabel")}
              tooltip={<TooltipWithIcon content={t("quickrank:ratingRightLabelTooltip")} />}
            >
              <TextInput
                icon={<QuestionMarkCircleIcon />}
                placeholder={t("quickrank:ratingRightLabelPlaceholder") ?? ""}
                value={ratingRightLabel || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setRatingRightLabel(e.target.value)}
                onBlur={handleSave}
                size="small"
                className={styles.input}
              />
            </Section>
          </>
        )}
      </Section>

      {isQuickPickGame(game) && (
        <Section title={t("project:gameSettings.form.preferences")}>
          <ToggleInput
            className={styles.toggleWrapper}
            value={showLeaderboard}
            name="show-leaderboard"
            label="Show leaderboard"
            onChange={handleShowLeaderboardChange}
            tooltip={<TooltipWithIcon content={t("project:gameSettings.form.showLeaderboardTooltip")} />}
          />

          {showLeaderboard && (
            <Accordion title={t("project:gameSettings.form.leaderboardConfig")}>
              <LeaderboardConfigSection
                leaderboardConfiguration={game.configuration.leaderboardConfiguration}
                onUpdate={handleLeaderboardConfigurationChange}
              />
            </Accordion>
          )}
        </Section>
      )}
    </>
  );
};
